import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import tracking from 'dibs-tracking';
import serverVars from 'server-vars';
import {
    getUserZipCode,
    getUserSessionCountryCode,
} from 'dibs-buyer-layout/exports/regionalInfoHelpers';
import { CUSTOMER_TYPE_TRADE } from '../constants/userConstants';
import { getPageviewData } from '../utils/tracking/searchBrowse/getPageviewData';

import { BuySharedHead } from './BuySharedHead/BuySharedHead';
import { SharedUserAndRelayVarLoader } from '../shared/SharedUserAndRelayVarLoader';
import { BuySharedBreadcrumbs } from './BuySharedBreadcrumbs/BuySharedBreadcrumbs';
import { BuySharedTitle } from './BuySharedTitle/BuySharedTitle';
import { BuySharedResultContainer } from './BuySharedResultContainer/BuySharedResultContainer';
import { BuySharedPagination } from './BuySharedPagination/BuySharedPagination';
import { BuySharedDescription } from './BuySharedDescription/BuySharedDescription';
import { BuySharedNullSearchResults } from './BuySharedNullSearchResults/BuySharedNullSearchResults';
import { BuySharedFrequentlyAskedQuestions } from './BuySharedFrequentlyAskedQuestions/BuySharedFrequentlyAskedQuestions';
import { BuySharedMoreWaysToBrowse } from './BuySharedMoreWaysToBrowse/BuySharedMoreWaysToBrowse';

import { BuySharedContentModuleRegular } from './BuySharedContentModuleRegular/BuySharedContentModuleRegular';

import styles from './BuySharedLayout.scss';

class BuySharedLayoutComponent extends Component {
    constructor() {
        super();

        this.handleUserRefetch = this.handleUserRefetch.bind(this);
    }

    componentDidMount() {
        const { viewer } = this.props;
        const { buyPage } = viewer;
        const { pageType, totalResults, appliedFilters } = buyPage;
        tracking.trackPageview({
            additional: {
                ...getPageviewData({
                    pageType,
                    totalResults,
                    appliedFilters,
                }),
            },
        });
    }

    handleUserRefetch(variables, renderVariables, callback) {
        /**
         * Need to refetch sb items for trade users, the price depends on the `isTrade` variable
         * being set.
         */
        const fetchBuy = variables.isTrade && serverVars.get('userType') !== CUSTOMER_TYPE_TRADE;
        const userZipCode = getUserZipCode();
        const userCountryCode = getUserSessionCountryCode();
        this.props.relay.refetch(
            { ...variables, userZipCode, userCountryCode, fetchRegionalInfo: true, fetchBuy },
            renderVariables,
            callback
        );
    }

    render() {
        const { viewer, relay } = this.props;
        const { buyPage } = viewer;
        const { internalBuyLinks, browseContentModule } = buyPage;

        const showNullSearchResultPage = !buyPage.totalResults;

        return (
            <Fragment>
                <BuySharedHead buyPage={buyPage} />
                <SharedUserAndRelayVarLoader onUserRefetch={this.handleUserRefetch} />

                {showNullSearchResultPage ? (
                    <BuySharedNullSearchResults viewer={viewer} buyPage={buyPage} />
                ) : (
                    <Fragment>
                        <BuySharedBreadcrumbs buyPage={buyPage} />
                        <BuySharedTitle buyPage={buyPage} />
                        <div className={styles.container}>
                            <BuySharedContentModuleRegular
                                browseContentModule={browseContentModule}
                            />
                            <BuySharedResultContainer
                                viewer={viewer}
                                buyPage={buyPage}
                                relay={relay}
                            />
                            <BuySharedPagination buyPage={buyPage} />
                            <BuySharedDescription buyPage={buyPage} />
                        </div>

                        <BuySharedMoreWaysToBrowse
                            internalBuyLinks={internalBuyLinks}
                            viewer={viewer}
                        />

                        <BuySharedFrequentlyAskedQuestions buyPage={buyPage} />
                    </Fragment>
                )}
            </Fragment>
        );
    }
}

BuySharedLayoutComponent.propTypes = {
    viewer: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
};

export const BuySharedLayout = createRefetchContainer(
    BuySharedLayoutComponent,
    {
        viewer: graphql`
            fragment BuySharedLayout_viewer on Viewer {
                buyPage: itemSearch(
                    uriRef: $uriRef
                    localeOrigin: $localeOrigin
                    first: $first
                    page: $page
                    buyPageId: $rootId
                ) {
                    totalResults
                    pageType
                    totalResults
                    appliedFilters {
                        name
                        values {
                            displayName
                            urlLabel
                            code
                        }
                    }
                    browseContentModule {
                        ...BuySharedContentModuleRegular_browseContentModule
                    }
                    ...BuySharedBreadcrumbs_buyPage
                    ...BuySharedTitle_buyPage
                    ...BuySharedResultContainer_buyPage
                    ...BuySharedPagination_buyPage
                    ...BuySharedDescription_buyPage
                    ...BuySharedHead_buyPage
                    ...BuySharedNullSearchResults_buyPage
                    ...BuySharedFrequentlyAskedQuestions_buyPage
                    internalBuyLinks {
                        ...BuySharedMoreWaysToBrowse_internalBuyLinks
                    }
                }
                ...BuySharedResultContainer_viewer
                ...BuySharedNullSearchResults_viewer
                ...BuySharedMoreWaysToBrowse_viewer
            }
        `,
    },
    graphql`
        query BuySharedLayoutRefetchQuery(
            $uriRef: String!
            $localeOrigin: String!
            $isTrade: Boolean!
            $showSeller: Boolean!
            $first: Int!
            $page: Int!
            $rootId: String!
            $fetchBuy: Boolean!
            $userZipCode: String = "0"
            $userCountryCode: String = ""
            $fetchRegionalInfo: Boolean = false
        ) {
            viewer {
                buyPage: itemSearch(
                    uriRef: $uriRef
                    localeOrigin: $localeOrigin
                    first: $first
                    page: $page
                    buyPageId: $rootId
                ) @include(if: $fetchBuy) {
                    ...BuySharedResultContainer_buyPage
                }
                ...BuySharedResultContainer_viewer
            }
        }
    `
);
